// TAPINTO® Editor Frontend - API Service
// Version: 1.0
// Date: 2025-10-01

import { 
  APIResponse, 
  LoginRequest, 
  LoginResponse, 
  RefreshResponse, 
  CreatePageRequest, 
  UpdatePageRequest,
  Page,
  PageFilters,
  Asset,
  AssetFilters,
  PageVersion
} from '../types';

class APIService {
  private baseURL: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private sessionId: string | null = null;

  constructor() {
    this.baseURL = 'https://tapinto-editor-worker.ian-wherry.workers.dev';
  }

  setTokens(accessToken: string, refreshToken: string, sessionId: string) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.sessionId = sessionId;
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    this.sessionId = null;
  }

  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle token refresh on 401
        if (response.status === 401 && this.refreshToken && this.sessionId) {
          const refreshed = await this.refreshAccessToken();
          if (refreshed) {
            // Retry the original request with new token
            headers.Authorization = `Bearer ${this.accessToken}`;
            const retryResponse = await fetch(url, {
              ...options,
              headers,
            });
            return await retryResponse.json();
          }
        }
        
        return {
          success: false,
          error: data.error || 'Request failed',
          message: data.message
        };
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: 'Network error',
        message: 'Unable to connect to server'
      };
    }
  }

  private async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken || !this.sessionId) {
      return false;
    }

    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: this.refreshToken,
          session_id: this.sessionId
        }),
      });

      if (response.ok) {
        const data: APIResponse<RefreshResponse> = await response.json();
        if (data.success && data.data) {
          this.accessToken = data.data.access_token;
          return true;
        }
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }

    // Clear tokens on refresh failure
    this.clearTokens();
    return false;
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<APIResponse<LoginResponse>> {
    const response = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    if (response.success && response.data) {
      this.setTokens(
        response.data.access_token,
        response.data.refresh_token,
        response.data.session_id
      );
    }

    return response;
  }

  async logout(): Promise<APIResponse> {
    const response = await this.request('/auth/logout', {
      method: 'POST',
    });

    this.clearTokens();
    return response;
  }

  async getPermissions(): Promise<APIResponse<{ role: string; permissions: any }>> {
    return this.request('/auth/permissions');
  }

  // Pages
  async getPages(filters?: PageFilters): Promise<APIResponse<{ pages: Page[]; total: number; limit: number; offset: number }>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, String(value));
        }
      });
    }
    
    return this.request(`/pages?${params.toString()}`);
  }

  async getPage(pageId: string): Promise<APIResponse<Page>> {
    return this.request(`/pages/${pageId}`);
  }

  async createPage(pageData: CreatePageRequest): Promise<APIResponse<{ page_id: string; created_at: string }>> {
    return this.request('/pages', {
      method: 'POST',
      body: JSON.stringify(pageData),
    });
  }

  async updatePage(pageId: string, updates: UpdatePageRequest): Promise<APIResponse<{ page_id: string; version: number; updated_at: string }>> {
    return this.request(`/pages/${pageId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async publishPage(pageId: string): Promise<APIResponse<{ page_id: string; status: string; published_at: string; live_url: string }>> {
    return this.request(`/pages/${pageId}/publish`, {
      method: 'POST',
    });
  }

  async getPageVersions(pageId: string): Promise<APIResponse<{ versions: PageVersion[] }>> {
    return this.request(`/pages/${pageId}/versions`);
  }

  async restorePageVersion(pageId: string, version: number): Promise<APIResponse<{ page_id: string; restored_version: number; new_version: number }>> {
    return this.request(`/pages/${pageId}/restore/${version}`, {
      method: 'POST',
    });
  }

  // Assets
  async getAssets(filters?: AssetFilters): Promise<APIResponse<{ assets: Asset[]; total: number; limit: number; offset: number }>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, String(value));
        }
      });
    }
    
    return this.request(`/assets?${params.toString()}`);
  }

  async getAsset(assetId: string): Promise<APIResponse<Asset>> {
    return this.request(`/assets/${assetId}`);
  }

  async uploadAsset(file: File, metadata?: { tags?: string; alt_text?: string }): Promise<APIResponse<{ asset_id: string; cdn_url: string; thumbnail_url?: string; filename: string; type: string; size_bytes: number }>> {
    const formData = new FormData();
    formData.append('file', file);
    if (metadata?.tags) formData.append('tags', metadata.tags);
    if (metadata?.alt_text) formData.append('alt_text', metadata.alt_text);

    return this.request('/assets/upload', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  }

  async updateAsset(assetId: string, updates: { tags?: string; alt_text?: string }): Promise<APIResponse<{ asset_id: string; updated_at: string }>> {
    return this.request(`/assets/${assetId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteAsset(assetId: string): Promise<APIResponse> {
    return this.request(`/assets/${assetId}`, {
      method: 'DELETE',
    });
  }

  async searchAssets(query: string, filters?: { type?: string; tags?: string }): Promise<APIResponse<{ assets: Asset[]; total: number; query: string }>> {
    const params = new URLSearchParams({ query });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, String(value));
        }
      });
    }
    
    return this.request(`/assets/search?${params.toString()}`);
  }
}

export const apiService = new APIService();
